
import React from "react";
import { CaseStatus } from "../../../api/cases";
import { cn } from "../../../lib/utils";

interface CaseStatusBadgeProps {
  status: CaseStatus;
  className?: string;
}

const getStatusStyles = (status: CaseStatus): { pill: string; dot: string } => {
  switch (status) {
    case CaseStatus.OPEN:
      return {
        pill: "bg-[#E0F2FE] text-[#0284C7]",
        dot: "bg-[#0284C7]",
      };

    case CaseStatus.IN_PROGRESS:
      return {
        pill: "bg-[#FEF9C3] text-[#CA8A04]",
        dot: "bg-[#CA8A04]",
      };

    case CaseStatus.DECLARED_AS_NO_FRAUD:
      return {
        pill: "bg-[#DCFCE7] text-[#16A34A]",
        dot: "bg-[#16A34A]",
      };

    case CaseStatus.DECLARED_AS_FRAUD:
    case CaseStatus.DECLARED_AS_ATTEMPTED_FRAUD:
      return {
        pill: "bg-[#FEE2E2] text-[#DC2626]",
        dot: "bg-[#DC2626]",
      };

    case CaseStatus.CLOSED:
      return {
        pill: "bg-[#EBEEFE] text-[#304ADD]",
        dot: "bg-[#304ADD]",
      };

    case CaseStatus.REJECTED:
      return {
        pill: "bg-[#F5F5F5] text-[#737373]",
        dot: "bg-[#737373]",
      };

    case CaseStatus.DEACTIVATED:
      return {
        pill: "bg-[#F3E8FF] text-[#7E22CE]",
        dot: "bg-[#7E22CE]",
      };

    default:
      return {
        pill: "bg-[#F5F5F5] text-[#737373]",
        dot: "bg-[#737373]",
      };
  }
};

export const textMap: Record<CaseStatus, string> = {
  [CaseStatus.OPEN]: "Open",
  [CaseStatus.IN_PROGRESS]: "In Progress",
  [CaseStatus.DECLARED_AS_NO_FRAUD]: "Declared as No Fraud",
  [CaseStatus.DECLARED_AS_FRAUD]: "Declared as Fraud",
  [CaseStatus.DECLARED_AS_ATTEMPTED_FRAUD]: "Declared as Attempted Fraud",
  [CaseStatus.CLOSED]: "Closed",
  [CaseStatus.REJECTED]: "Rejected",
  [CaseStatus.DEACTIVATED]: "Deactivated",
};

export const STATUS_OPTIONS: CaseStatus[] = [
  CaseStatus.OPEN,
  CaseStatus.IN_PROGRESS,
  CaseStatus.DECLARED_AS_NO_FRAUD,
  CaseStatus.DECLARED_AS_FRAUD,
  CaseStatus.DECLARED_AS_ATTEMPTED_FRAUD,
  CaseStatus.DEACTIVATED,
  CaseStatus.CLOSED,
  CaseStatus.REJECTED,
];

const CaseStatusBadge: React.FC<CaseStatusBadgeProps> = ({
  status,
  className,
}) => {
  const styles = getStatusStyles(status);
  const basePillClasses =
    "inline-flex items-center h-5 w-fit px-2 py-0.5 gap-1 rounded-md border border-current/20 font-sans text-xs leading-4 font-medium tracking-normal whitespace-nowrap";
  const baseDotClasses = "h-2 w-2 rounded-full";

  return (
    <span className={cn(basePillClasses, styles.pill, className)}>
      <span className={cn(baseDotClasses, styles.dot)} />
      {textMap[status]}
    </span>
  );
};

export default CaseStatusBadge;
